import React, { useEffect, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  ColumnDef,
  SortDirection,
} from "@tanstack/react-table";
import "./styles.css"; // Import the CSS file
import {
  collection,
  getDoc,
  deleteDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import Swal from "sweetalert2";
import { database } from "./firebaseConfig";
import { useLanguage } from "./languageContext";


interface RowData {
  id: string | null | undefined;
  camera_id: string | null | undefined;
  common_name: string | null | undefined;
  date: string | null | undefined;
  probability: number| null | undefined;
  scientific_name: string | null | undefined;
  temperature: string | null | undefined;
  time: string | null | undefined;
}

enum CustomSortDirection {
  Ascending = "asc",
  Descending = "desc",
}
interface Props {
  data: RowData[];
  columns: ColumnDef<RowData, any>[]; // Adjust the type here
  CollectionName: string; // New additional value
}
interface CustomColumnSort {
  id: string;
  desc: boolean;
}

export default function EditImageDisplayData({
  data,
  columns,
}: Props): JSX.Element {
  const [sorting, setSorting] = useState<CustomColumnSort[]>([]); // Use CustomColumnSort type
  const [filtering, setFiltering] = useState<string>("");
  const [isEditing, setIsEditing] = useState(false);
  const [camera_id, setCamera_id] = useState("");
  const [date, setDate] = useState("");
  const [common_name, setCommon_name] = useState("");
  const [scientific_name, setScientific_name] = useState("");
  const [probability, setProbability] = useState("");
  const [temperature, setTemperature] = useState("");
  const [time, setTime] = useState("");
  const [editCamera_id, editsetCamera_id] = useState("");
  const [editDate, editsetDate] = useState("");
  const [editCommon_name, editsetCommon_name] = useState("");
  const [editScientific_name, editsetScientific_name] = useState("");
  const [editProbability, editsetProbability] = useState(0);
  const [editTemperature, editsetTemperature] = useState("");
  const [editTime, editsetTime] = useState("");
  const [selectedRow, setSelectedRow] = useState<RowData | null>(null);

  const handleRowDelete = async (idToDelete) => {
    try {
      // Ask the user for confirmation using Swal
      const swalResult = await Swal.fire({
        title: "Confirmar la eliminación",
        text: "Está seguro de que desea eliminar este documento?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
      });

      if (swalResult.isConfirmed) {
        console.log(idToDelete);
        await deleteDoc(doc(database, "IR_OCR", idToDelete));
        Swal.fire("¡Eliminado!", "El documento ha sido eliminado.", "success");
      } else {
        // User canceled the delete operation
        console.log("Eliminar operación cancelada por el usuario.");
      }
    } catch (error) {
      console.error("Error al eliminar el documento: ", error);
    }
  };

  useEffect(() => {
    if (selectedRow) {
      console.log("Selected Row: ", selectedRow);
      editsetCamera_id(selectedRow.camera_id || "");
      editsetCommon_name(selectedRow.common_name || "");
      editsetDate(selectedRow.date || "");
      editsetProbability(selectedRow.probability || 0);
      editsetScientific_name(
        selectedRow.scientific_name !== null && selectedRow.scientific_name !== undefined
          ? selectedRow.scientific_name.toString()
          : ""
      );
      editsetTemperature(selectedRow.temperature || "");
      editsetTime(selectedRow.time || "");
    }
  }, [selectedRow]);

  const handleRowEdit = async (idToEdit) => {
    // Update the database with selectedRow data
    if (selectedRow) {
      try {
        // Update the data in the database based on selectedRow
        const userDoc = doc(database, "IR_OCR", idToEdit);
        const updatedData = {
          id: idToEdit,
          camera_id: editCamera_id || "",
          date: editDate || "",
          common_name: editCommon_name || "",
          scientific_name: editScientific_name || "",
          probability: editProbability || 0,
          temperature: editTemperature || "",
          time: editTime || "",
        };
        await updateDoc(userDoc, updatedData);
        Swal.fire("¡Actualizado!", "Tus cambios se han guardado.", "success");
        setSelectedRow(null); // Reset the selectedRow state after saving
        // getUsers(); // Fetch updated data
      } catch (error) {
        console.error("Error al actualizar datos:", error);
        Swal.fire(
          "Error",
          "Se produjo un error al actualizar los datos.",
          "error"
        );
      }
    }
  };
  const editUser = (id: string) => {
    const selected = data.find((data) => data.id === id);
    setSelectedRow(selected || null);
  };

  // const getid = (row) => {
  //   const idCell = row.cells.find((cell) => cell.column.id === 'id');

  //   if (idCell) {
  //     const idCellValue = idCell.value;
  //     console.log('Edit ID:', idCellValue);
  //     // Implement your edit logic here, using the idCellValue
  //   }
  // };

  const table = useReactTable<RowData>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),

    state: {
      sorting: sorting,
      globalFilter: filtering,
    },
    onSortingChange: (newSorting) =>
      setSorting(newSorting as CustomColumnSort[]), // Update type conversion
    onGlobalFilterChange: setFiltering,
  });

  const { language, toggleLanguage } = useLanguage();
  
  return (
    <div>
      {selectedRow && (
        <div className="edit-details-container">
          <h6>Edit Details</h6>
          <div className="input-row">
            <div>
              <h3>Set Camera ID:</h3>
              <input
                type="text"
                placeholder="setCamera_id"
                value={editCamera_id || ""}
                onChange={(e) => editsetCamera_id(e.target.value)}
              />
            </div>{" "}
            <div>
              <h3>Set Common Name:</h3>
              <input
                type="text"
                placeholder="setCommon_name"
                value={editCommon_name || ""}
                onChange={(e) => editsetCommon_name(e.target.value)}
              />
            </div>{" "}
            <div>
              <h3>Set Date:</h3>
              <input
                type="text"
                placeholder="setDate"
                value={editDate || ""}
                onChange={(e) => editsetDate(e.target.value)}
              />
            </div>{" "}
            <div>
              <h3>Set Probability:</h3>
              <input
                type="text"
                placeholder="setProbability"
                value={editProbability}
                onChange={(e) => editsetProbability(Number(e.target.value))}
              />
            </div>{" "}
            <div>
              <h3>Set Scientific Name:</h3>
              <input
                type="text"
                placeholder="setScientific_name"
                value={editScientific_name || ""}
                onChange={(e) => editsetScientific_name(e.target.value)}
              />
            </div>{" "}
            <div>
              <h3>Set Temperature:</h3>
              <input
                type="text"
                placeholder="setTemperature"
                value={editTemperature}
                onChange={(e) => editsetTemperature(e.target.value)}
              />
            </div>{" "}
            <div>
              <h3>Set Time:</h3>
              <input
                type="text"
                placeholder="setTime"
                value={editTime}
                onChange={(e) => editsetTime(e.target.value)}
              />
            </div>{" "}
          </div>

          <div className="button-row">
            {/* Add other input fields for editing other properties */}
            <button
              className="refresh-button"
              // TODO: add ID row in dataset to commnet this out
              onClick={() => handleRowEdit(selectedRow.id)}
            >
              {language === "english" ? "Save" : "Ahorrar"}
            </button>
            <button
              className="light-red-button"
              onClick={() => setSelectedRow(null)}
            >
              {language === "english" ? "Close" : "Cerrar"}
            </button>
          </div>
        </div>
      )}
      <div className="table-scroll">
        <table>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {header.isPlaceholder ? null : (
                      <div>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getIsSorted() ===
                        CustomSortDirection.Ascending
                          ? "🔼"
                          : "🔽"}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  console.log(cell),
                  <td key={cell.id}>
                    {flexRender(cell.row.original[cell.column.id.replace(" ","_").toLowerCase()], {
                      row: cell.row,
                      column: cell.column,
                    })}
                  </td>
                ))}
                <td>
                  <button
                    className="light-red-button"
                    onClick={() => handleRowDelete(row.original.id)}
                  >
                    {language === "english" ? "Delete" : "Borrar"}
                  </button>
                </td>
                <td>
                  <button
                    className="light-green-button"
                    onClick={() => editUser(row.original.id)}
                  >
                    {language === "english" ? "Edit" : "Editar"}
                  </button>
                  {/* <button onClick={() => console.log((row.original.id)) }>Edit</button> */}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ textAlign: "center" }}>
        <button
          className="verylight-blue-button"
          onClick={() => table.setPageIndex(0)}
        >
          {language === "english" ? "First page" : "Primera página"}
        </button>
        <button
          className="verylight-blue-button"
          disabled={!table.getCanPreviousPage()}
          onClick={() => table.previousPage()}
        >
          {language === "english" ? "Previous page" : "Pagina anterior"}{" "}
        </button>
        <button
          className="verylight-blue-button"
          disabled={!table.getCanNextPage()}
          onClick={() => table.nextPage()}
        >
          {language === "english" ? "Next page" : "Siguiente página"}{" "}
        </button>
        <button
          className="verylight-blue-button"
          onClick={() => table.setPageIndex(table.getPageCount() - 1)}
        >
          {language === "english" ? "Last page" : "Última página"}{" "}
        </button>
      </div>
    </div>
  );
}
